// C:\Users\ADMIN\Desktop\ERP\myerp\my-frontend\src\pages\expense\business\ExpenseBusinessNew.jsx

import React, { useEffect, useMemo, useState, useRef } from "react";
import styles from "./ExpenseBusinessNew.module.css";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";

import ExpenseItemPanel from "./ExpenseItemPanel";
import VendorPanel from "./VendorPanel";
import SalaryPanel from "./SalaryPanel";



export default function ExpenseBusinessNew() {
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [executionDate, setExecutionDate] = useState("");
  const [seq, setSeq] = useState("001");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [remark, setRemark] = useState("");


  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);

  const [categoryLabel, setCategoryLabel] = useState("");
  const [categoryId, setCategoryId] = useState(null);

  const [itemLabel, setItemLabel] = useState("");
  const [itemId, setItemId] = useState(null);

  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldId, setFieldId] = useState(null);

  const [showCategoryList, setShowCategoryList] = useState(false);
  const [showItemList, setShowItemList] = useState(false);
  const [showFieldList, setShowFieldList] = useState(false);

  const [catHighlightIdx, setCatHighlightIdx] = useState(-1);
  const [itemHighlightIdx, setItemHighlightIdx] = useState(-1);
  const [fieldHighlightIdx, setFieldHighlightIdx] = useState(-1);

  const [hasAdditional, setHasAdditional] = useState("없음");
  const [additionalCount, setAdditionalCount] = useState("");
  const [additionalUnit, setAdditionalUnit] = useState("종");

  const [partners, setPartners] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [partnerList, setPartnerList] = useState([
    { name: "", id: null, show: false, highlight: -1 },
  ]);

  const [itemList, setItemList] = useState([]);
  const [salaryList, setSalaryList] = useState([]);

  useEffect(() => {
    try {
      let supply = 0;
      let vatSum = 0;
      let salaryTotal = 0;

      itemList.forEach((it) => {
        supply += Number(it.supply || it.supply_amount || 0);
        vatSum += Number(it.vat || 0);
      });

      salaryList.forEach((s) => {
        salaryTotal += Number((s.totalSalary || "0").toString().replace(/,/g, ""));
      });

      setSupplyAmount(String(supply + salaryTotal));
      setVat(String(vatSum));
    } catch (err) {
      console.error("❌ 자동 합계 계산 오류:", err);
    }
  }, [itemList, salaryList]);

  const [supplyAmount, setSupplyAmount] = useState("");
  const [vat, setVat] = useState("");
  const totalAmount = useMemo(() => {
    const s = Number(supplyAmount) || 0;
    const v = Number(vat) || 0;
    return s + v;
  }, [supplyAmount, vat]);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");
  const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
  const PAY_METHOD_TO_CODE = {
    계좌이체: "transfer",
    카드결제: "card",
    현금결제: "cash",
  };

  // ✅ 단축키용 ref
  const draftDateRef     = useRef(null); // ALT+1
  const categoryInputRef = useRef(null); // ALT+2
  const itemInputRef     = useRef(null); // ALT+3
  const fieldInputRef    = useRef(null); // ALT+4
  const vendorPanelRef   = useRef(null); // ALT+5
  const itemPanelRef     = useRef(null); // ALT+6
  const salaryPanelRef   = useRef(null); // ALT+7

  // 드롭다운 감지용 ref
  const categoryRef = useRef(null);
  const itemRef     = useRef(null);
  const fieldRef    = useRef(null);

  // ✅ 드롭다운 목록 스크롤 컨테이너 ref — 하이라이트 이동 시 자동 스크롤용
  const categoryListRef = useRef(null);
  const itemListRef     = useRef(null);
  const fieldListRef    = useRef(null);

  // ✅ ↓/↑로 하이라이트가 이동해도 화면 밖으로 나가지 않도록 항상 보이는 위치로 스크롤
  useEffect(() => {
    categoryListRef.current?.children?.[catHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [catHighlightIdx]);
  useEffect(() => {
    itemListRef.current?.children?.[itemHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [itemHighlightIdx]);
  useEffect(() => {
    fieldListRef.current?.children?.[fieldHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [fieldHighlightIdx]);

  // ✅ 전역 단축키 등록
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.altKey) return;
      switch (e.key) {
        case "1":
          e.preventDefault();
          draftDateRef.current?.focus();
          break;
        case "2":
          e.preventDefault();
          categoryInputRef.current?.focus();
          setShowCategoryList(true);
          break;
        case "3":
          e.preventDefault();
          itemInputRef.current?.focus();
          setShowItemList(true);
          break;
        case "4":
          e.preventDefault();
          fieldInputRef.current?.focus();
          setShowFieldList(true);
          break;
        case "5":
          e.preventDefault();
          vendorPanelRef.current?.querySelector("input")?.focus();
          break;
        case "6":
          e.preventDefault();
          itemPanelRef.current?.querySelector("input")?.focus();
          break;
        case "7":
          e.preventDefault();
          salaryPanelRef.current?.querySelector("input")?.focus();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ✅ keyDown 핸들러에서 참조하므로 먼저 선언
  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(categoryLabel.toLowerCase())
  );
  const filteredItems = items.filter((i) =>
    i.name.toLowerCase().includes(itemLabel.toLowerCase())
  );
  const filteredFields = fields.filter((f) =>
    f.name.toLowerCase().includes(fieldLabel.toLowerCase())
  );

  const handleCategoryKeyDown = (e) => {
    if (e.key === "Escape") { setShowCategoryList(false); return; }
    if (!showCategoryList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCatHighlightIdx((i) => Math.min(i + 1, filteredCategories.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCatHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (catHighlightIdx >= 0 && filteredCategories[catHighlightIdx]) {
        const c = filteredCategories[catHighlightIdx];
        setCategoryLabel(c.name);
        setCategoryId(c.id);
        setShowCategoryList(false);
        setCatHighlightIdx(-1);
      }
    }
  };
  const handleItemKeyDown = (e) => {
    if (e.key === "Escape") { setShowItemList(false); return; }
    if (!showItemList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setItemHighlightIdx((i) => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setItemHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (itemHighlightIdx >= 0 && filteredItems[itemHighlightIdx]) {
        const it = filteredItems[itemHighlightIdx];
        setItemLabel(it.name);
        setItemId(it.id);
        setShowItemList(false);
        setItemHighlightIdx(-1);
      }
    }
  };
  const handleFieldKeyDown = (e) => {
    if (e.key === "Escape") { setShowFieldList(false); return; }
    if (!showFieldList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFieldHighlightIdx((i) => Math.min(i + 1, filteredFields.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFieldHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (fieldHighlightIdx >= 0 && filteredFields[fieldHighlightIdx]) {
        const f = filteredFields[fieldHighlightIdx];
        setFieldLabel(f.name);
        setFieldId(f.id);
        setShowFieldList(false);
        setFieldHighlightIdx(-1);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const resCat    = await api.get("/expense/usage/category/");
        const resItem   = await api.get("/expense/usage/item/");
        const resField  = await api.get("/expense/usage/field/");
        setCategories(resCat.data);
        setItems(resItem.data);
        setFields(resField.data);

        setLoadingVendors(true);
        const resVendor = await api.get("vendors/");
        setPartners(Array.isArray(resVendor.data) ? resVendor.data : resVendor.data.results || []);
      } catch (err) {
        console.error("❌ 코드 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    })();
  }, []);

  useEffect(() => {
    setExecutionDate(draftDate);
  }, [draftDate]);

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);

  const draftNo = useMemo(
    () => `금천일자리(주)경영지출-${yymmdd}-${seq}호`,
    [yymmdd, seq]
  );

  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    async function fetchSeq() {
      try {
        const { data } = await api.get(`/expense/business/next-seq/?date=${draftDate}`);
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (err) {
        console.error("❌ next-seq 오류:", err);
      }
    }
    fetchSeq();
    return () => { ignore = true; };
  }, [draftDate, seqRefreshTick]);

  useEffect(() => {
    if (!executionDate || !categoryLabel || !itemLabel || !fieldLabel) return;
    const d = new Date(executionDate);
    const formatted = `${d.getFullYear()}년 ${String(d.getMonth() + 1).padStart(2, "0")}월 ${String(d.getDate()).padStart(2, "0")}일`;
    let mid = `${categoryLabel}(${itemLabel}`;
    if (hasAdditional === "있음" && Number(additionalCount) > 0) {
      mid += ` 외 ${additionalCount}${additionalUnit}`;
    }
    mid += `)`;
    setTitle(`${formatted} ${mid} ${fieldLabel}`);
  }, [executionDate, categoryLabel, itemLabel, hasAdditional, additionalCount, additionalUnit, fieldLabel]);

  async function onSubmit(e) {
    e.preventDefault();
    if (saving) return;

    if (!executionDate) return alert("집행예정일을 입력해주세요.");

    const validVendor = partnerList.some((v) => v.name.trim() !== "");
    if (!validVendor) return alert("최소 1개 이상의 거래처가 필요합니다.");
    if (!supplyAmount || Number(supplyAmount) === 0) {
      const confirmed = window.confirm("공급가가 0원입니다. 맞습니까?\n\n확인 = 저장하기 / 취소 = 수정하기");
      if (!confirmed) return;
    }
    setSaving(true);

    const cleanedPartners = partnerList
      .filter((p) => p.name.trim() !== "")
      .map((p) => ({ name: p.name, id: p.id }));

    const payload = {
      draft_date:     draftDate,
      execution_date: executionDate,
      partner_name:   partnerList[0]?.name || "",
      partner_id:     partnerList[0]?.id || null,
      partners:       cleanedPartners,
      items:          itemList,
      salaries:       salaryList,
      service_type:   categoryLabel,
      service_content: itemLabel,
      service_location: fieldLabel,
      other_count:    hasAdditional === "있음" ? Number(additionalCount) || 0 : 0,
      title,
      supply_amount:  Number(supplyAmount) || 0,
      vat:            Number(vat) || 0,
      pay_method:     PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      remark,
    };

    try {
      const { data } = await api.post("expense/business/", payload);
      setSavedId(data.id);
      alert(`✅ 저장 완료!\n기안번호: ${data.draft_no}`);

      const next = await api.get(`/expense/business/next-seq/?date=${draftDate}`);
      setSeq(String(next.data.next_seq).padStart(3, "0"));

      setExecutionDate("");
      setCategoryLabel(""); setCategoryId(null);
      setItemLabel("");     setItemId(null);
      setFieldLabel("");    setFieldId(null);
      setPartnerList([{ name: "", id: null, show: false, highlight: -1 }]);
      setSupplyAmount(""); setVat(""); setRemark(""); setTitle("");
      setItemList([]); setSalaryList([]);
      setHasAdditional("없음"); setAdditionalCount("");
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert("서버 저장 실패: " + JSON.stringify(err.response?.data || err));
    } finally {
      setSaving(false);
    }
  }



  return (
    <div className={styles.wrap}>
      <div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "0px" }}>
        <div style={{ flex: "0 0 10%" }}>
          <div className={styles.pane}>
            <form className={styles.form} onSubmit={onSubmit}>
              <h3>(구) 경영팀 지출기안서 작성</h3>

              <Row label="기안번호(자동완성)">
                <input value={draftNo} readOnly />
              </Row>

              <Row label="제목(자동완성)">
                <input value={title} readOnly placeholder="자동 생성됩니다." />
              </Row>

              <Row label="사업구분">
                <input value="경영팀" readOnly />
              </Row>

              {/* ✅ ALT+1 — 라벨에 표시 유지 */}
              <Row label="작성일 [ALT+1]">
                <input
                  type="date"
                  ref={draftDateRef}
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                />
              </Row>

              <Row label="집행예정일">
                <input
                  type="date"
                  value={executionDate}
                  onChange={(e) => setExecutionDate(e.target.value)}
                />
              </Row>

              {/* ✅ ALT+2 — 입력창 내부 우측에 힌트 */}
              <Row label="사용내역 구분명">
                <div className={styles.partnerRow} ref={categoryRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={categoryInputRef}
                      placeholder="[ALT+2] 주재료, 소모품 등"

                      value={categoryLabel}
                      onChange={(e) => {
                        setCategoryLabel(e.target.value);
                        setShowCategoryList(true);
                        setCatHighlightIdx(-1);
                      }}
                      onFocus={() => setShowCategoryList(true)}
                      onKeyDown={handleCategoryKeyDown}
                    />
                    <button
                      type="button"
                      className={styles.partnerButton}
                      onClick={() => setShowCategoryList((v) => !v)}
                    >
                      ▼
                    </button>
                  </div>
                  {showCategoryList && (
                    <div className={styles.partnerBox} ref={categoryListRef}>
                      {filteredCategories.length
                        ? filteredCategories.map((c, i) => (
                            <div
                              key={c.id}
                              className={`${styles.partnerItem} ${i === catHighlightIdx ? styles.activeItem : ""}`}
                              onClick={() => {
                                setCategoryLabel(c.name);
                                setCategoryId(c.id);
                                setShowCategoryList(false);
                              }}
                            >
                              {c.name}
                            </div>
                          ))
                        : "일치 없음"}
                    </div>
                  )}
                </div>
              </Row>

              {/* ✅ ALT+3 — 입력창 내부 우측에 힌트 */}
              <Row label="사용내역 내용명">
                <div className={styles.partnerRow} ref={itemRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={itemInputRef}
                      placeholder="[ALT+3] 물품/용역명 입력"

                      value={itemLabel}
                      onChange={(e) => {
                        setItemLabel(e.target.value);
                        setShowItemList(true);
                        setItemHighlightIdx(-1);
                      }}
                      onFocus={() => setShowItemList(true)}
                      onKeyDown={handleItemKeyDown}
                    />
                    <button
                      type="button"
                      className={styles.partnerButton}
                      onClick={() => setShowItemList((v) => !v)}
                    >
                      ▼
                    </button>
                  </div>
                  {showItemList && (
                    <div className={styles.partnerBox} ref={itemListRef}>
                      {filteredItems.length
                        ? filteredItems.map((it, i) => (
                            <div
                              key={it.id}
                              className={`${styles.partnerItem} ${i === itemHighlightIdx ? styles.activeItem : ""}`}
                              onClick={() => {
                                setItemLabel(it.name);
                                setItemId(it.id);
                                setShowItemList(false);
                              }}
                            >
                              {it.name}
                            </div>
                          ))
                        : "일치 없음"}
                    </div>
                  )}
                </div>
              </Row>

              <Row label="내용 추가 유무">
                <div className={styles.inline}>
                  <label className={styles.check}>
                    <input type="checkbox" checked={hasAdditional === "없음"} onChange={() => setHasAdditional("없음")} /> 없음
                  </label>
                  <label className={styles.check}>
                    <input type="checkbox" checked={hasAdditional === "있음"} onChange={() => setHasAdditional("있음")} /> 있음
                  </label>
                  <input
                    type="number"
                    value={additionalCount}
                    onChange={(e) => setAdditionalCount(e.target.value)}
                    placeholder="숫자"
                    disabled={hasAdditional === "없음"}
                    style={{ width: "60px" }}
                  />
                  <label className={styles.check}>
                    <input type="checkbox" checked={additionalUnit === "종"} onChange={() => setAdditionalUnit("종")} disabled={hasAdditional === "없음"} /> 종
                  </label>
                  <label className={styles.check}>
                    <input type="checkbox" checked={additionalUnit === "명"} onChange={() => setAdditionalUnit("명")} disabled={hasAdditional === "없음"} /> 명
                  </label>
                </div>
              </Row>

              {/* ✅ ALT+4 — 입력창 내부 우측에 힌트 */}
              <Row label="사용내역 분야명">
                <div className={styles.partnerRow} ref={fieldRef}>
                  <div style={{ position: "relative" }}>
                    <input
                      ref={fieldInputRef}
                      placeholder="[ALT+4] 구입, 결제, 지급 등"

                      value={fieldLabel}
                      onChange={(e) => {
                        setFieldLabel(e.target.value);
                        setShowFieldList(true);
                        setFieldHighlightIdx(-1);
                      }}
                      onFocus={() => setShowFieldList(true)}
                      onKeyDown={handleFieldKeyDown}
                    />
                    <button
                      type="button"
                      className={styles.partnerButton}
                      onClick={() => setShowFieldList((v) => !v)}
                    >
                      ▼
                    </button>
                  </div>
                  {showFieldList && (
                    <div className={styles.partnerBox} ref={fieldListRef}>
                      {filteredFields.length
                        ? filteredFields.map((f, i) => (
                            <div
                              key={f.id}
                              className={`${styles.partnerItem} ${i === fieldHighlightIdx ? styles.activeItem : ""}`}
                              onClick={() => {
                                setFieldLabel(f.name);
                                setFieldId(f.id);
                                setShowFieldList(false);
                              }}
                            >
                              {f.name}
                            </div>
                          ))
                        : "일치 없음"}
                    </div>
                  )}
                </div>
              </Row>

              <Row label="금액(자동완성)">
                <div className={styles.amountBox}>
                  <input
                    value={supplyAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (!isNaN(raw)) setSupplyAmount(raw);
                    }}
                    placeholder="공급가"
                  />
                  +
                  <input
                    value={vat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (!isNaN(raw)) setVat(raw);
                    }}
                    placeholder="부가세"
                  />
                  =
                  <input
                    readOnly
                    value={totalAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    placeholder="합계"
                  />
                </div>
              </Row>

              <Row label="거래방법">
                <div className={styles.inline}>
                  {PAY_METHOD_LABELS.map((label) => (
                    <label key={label} className={styles.check}>
                      <input
                        type="checkbox"
                        checked={payMethodLabel === label}
                        onChange={() => setPayMethodLabel(label)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </Row>

              <Row label="비고">
                <textarea
                  rows={1}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="지출 메모 입력"
                />
              </Row>

              <div className={styles.actions}>
                <button type="submit" disabled={saving} className={styles.primaryBtn}>
                  {saving ? "저장 중..." : "저장"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div style={{ flex: "1", padding: "0px" }}>
          {/* ✅ ALT+5: 거래처 패널 — 우상단 힌트 */}
          <div ref={vendorPanelRef}>
            <VendorPanel
              vendors={partners}
              loadingVendors={loadingVendors}
              onChange={(list) => setPartnerList(list)}
            />
          </div>

          {/* ✅ ALT+6: 품목 패널 — 우상단 힌트 */}
          <div ref={itemPanelRef}>
            <ExpenseItemPanel onItemsChange={setItemList} />
          </div>

          {/* ✅ ALT+7: 인건비 패널 — 우상단 힌트 */}
          <div ref={salaryPanelRef}>
            <SalaryPanel onItemsChange={setSalaryList} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}